import { useNavigate } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AgentPanelShell } from '~/components/common/agent-panel-shell/agent-panel-shell';
import type { CompareViewMode } from '~/components/common/enrichment-compare-grid/compare-grid.types';
import { CompareViewCtx } from '~/components/common/enrichment-compare-grid/compare-view-mode-context';
import { CompareViewToggle } from '~/components/common/enrichment-compare-grid/compare-view-toggle';
import { SlidingPanel } from '~/components/common/sliding-panel/sliding-panel';
import { Button } from '~/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Tabs, TabsContent } from '~/components/ui/tabs';
import { EnrichmentPreviewProducts } from '~/features/content-enrichment/components/enrichment-preview-products';
import { CostApprovalPendingBanner } from '~/features/cost-estimate/components/cost-approval-pending-banner';
import { CostEstimateCard } from '~/features/cost-estimate/components/cost-estimate-card';
import type { CostActualPhase } from '~/features/cost-estimate/types/cost-estimate.types';
import { ExportDownloadButton } from '~/features/exports/components/export-download-button';
import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';
import { useMediaQuery } from '~/hooks/use-media-query';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import type { ExperimentDetailSearch } from '~/routes/_authenticated/_tenant/experiments/$id';

import { ExperimentChatbotPanel } from '../components/chatbot/experiment-chatbot-panel';
import { AnalyticsTimeoutBanner } from '../components/detail/analytics-timeout-banner';
import { DeliveryInProgressCard } from '../components/detail/delivery-in-progress-card';
import { ExperimentDetailHeader } from '../components/detail/experiment-detail-header';
import type { ExperimentDetailTab } from '../components/detail/experiment-detail-header';
import type { ExperimentDetailsSubTab } from '../components/detail/experiment-details-sub-tab';
import { ExperimentDetailsTab } from '../components/detail/experiment-details-tab';
import { ExperimentLoadError } from '../components/detail/experiment-load-error';
import { ExperimentTimeline } from '../components/detail/experiment-timeline';
import { RandomFallbackBanner } from '../components/detail/random-fallback-banner';
import { usePipelineStatus } from '../hooks/use-experiment-pipeline';
import { useExperiment, useExperimentTimeline } from '../hooks/use-experiments';
import type { PerformanceViewMode } from '../hooks/use-performance-view-mode';
import { usePerformanceViewMode } from '../hooks/use-performance-view-mode';
import { clearWrongSubsidiaryAttempt } from '../hooks/use-wrong-subsidiary-redirect';
import { PipelineStatus } from '../services/experiment-pipeline.service';
import type { AnalyticsGrain, AnalyticsSensitivity } from '../types/analytics-controls.types';
import type { ChatbotView } from '../types/chatbot-message.types';
import { ExperimentPhase, ExperimentStatus } from '../types/experiment.types';
import { encodeSegmentFilter } from '../utils/segment-filter';

interface Props {
  experimentId: string;
  /** Active main tab; URL-controlled so the view survives reload and sharing. */
  tab: ExperimentDetailSearch['tab'];
  /** Active Details sub-tab; URL-controlled so the view survives reload and sharing. */
  subTab: ExperimentDetailSearch['subTab'];
}

// Time-series bucket size and outlier-rate sensitivity used to be user-tunable
// via dropdowns + URL search params; both controls were removed, so these are
// now fixed defaults consumed by the Performance panel, CSV export, Health
// summary, and Chatbot.
const FIXED_GRAIN: AnalyticsGrain = 'day';
const FIXED_SENSITIVITY: AnalyticsSensitivity = 3;

/** Phases that belong in the content-enrichment funnel, not the timeline page. */
const PRE_LAUNCH_PHASES: string[] = [
  ExperimentPhase.CURRENT_CONTENT_SCORE,
  ExperimentPhase.GENERATE_SAMPLE_CONTENT,
  ExperimentPhase.REVIEW_AND_REFINE,
  ExperimentPhase.FULL_CATALOG_GENERATION,
  ExperimentPhase.ENRICHMENT_OVERVIEW,
];

export default function ExperimentDetailPage({ experimentId, tab, subTab }: Props) {
  const { data: experiment, isLoading, isError, error } = useExperiment(experimentId);
  const {
    data: checkpoints,
    isLoading: checkpointsLoading,
    isError: checkpointsError,
  } = useExperimentTimeline(experimentId);
  // Poll the pipeline-status row while the workflow is in flight; stop
  // polling once it reaches a terminal state to avoid burning requests on
  // an unchanging row. Falls back to the initial GET on cache miss.
  const { data: pipeline } = usePipelineStatus(experimentId, {
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const inFlight =
        status === PipelineStatus.IN_PROGRESS || status === PipelineStatus.AWAITING_APPROVAL;
      return inFlight ? 5000 : false;
    },
  });

  // Products tab: Table vs. feed-vs-enriched compare grid, flag-gated.
  const { isAllowed: canCompare } = useFeatureGate('enrichment_compare_grid');
  const [productsViewMode, setProductsViewMode] = useState<CompareViewMode>('table');

  // Pre-enrichment ("before") data comes from products/{connectionId}/list, so
  // the preview needs the GMC connection id and the experiment's pinned catalog
  // snapshot. Resolved here the same way the enrichment funnel's adapter does —
  // this tab renders outside the funnel, so there's no EnrichmentCtx to read.
  const { data: connectedConnectors } = useConnectedConnectors();
  const gmcConnectionId =
    connectedConnectors?.find((c) => c.code === 'GOOGLE_MERCHANT')?.connectionId ?? '';

  // Main tab and Details sub-tab are URL-controlled so the view survives reload
  // and can be shared via copy/paste. `replace: true` on navigate keeps the
  // browser history clean — tab switches don't stack as separate entries.
  const activeTab = tab as ExperimentDetailTab;
  const detailsSubTab = subTab as ExperimentDetailsSubTab;
  const [segment, setSegment] = useState('all');
  // Only the "group" option actually scopes data today — "remaining" (the
  // complement) has no encoding yet, so it falls through to the unscoped,
  // full-cohort result same as "all" until that's supported.
  const segmentFilter =
    segment === 'group' ? encodeSegmentFilter(experiment?.groupingFilter) : undefined;

  // `performanceViewMode` is the flag-driven default ('category' when the tenant
  // has category_performance enabled, 'default' otherwise). The user can override
  // it per-session via the "View as" dropdown; null means "use the flag default".
  // `experiment` may be undefined while loading; null falls back to 'default' safely.
  const performanceViewMode = usePerformanceViewMode(experiment?.platform ?? null);
  const [userViewMode, setUserViewMode] = useState<PerformanceViewMode | null>(null);
  const activeViewMode: PerformanceViewMode = userViewMode ?? performanceViewMode;
  // Show the "View as" dropdown only when the category dashboard flag is on.
  const showViewDropdown = performanceViewMode === 'category';

  // Chatbot mirrors the active tab + view mode. On the Performance tab with
  // 'category' view selected the chatbot narrates category data; 'default' maps
  // to 'performance' since ChatbotView has no 'default' variant.
  const view: ChatbotView =
    activeTab === 'timeline'
      ? 'timeline'
      : detailsSubTab === 'health'
        ? 'health'
        : activeViewMode === 'category'
          ? 'category'
          : 'performance';

  // `from` scopes navigate to this route so the `prev` search reducer receives
  // the fully typed ExperimentDetailSearch.
  const navigate = useNavigate({ from: '/experiments/$id' });

  // Once the experiment loads, clear any wrong-subsidiary auto-switch marker so
  // a later re-open of this deep link can auto-switch again.
  useEffect(() => {
    if (experiment) clearWrongSubsidiaryAttempt(experimentId);
  }, [experiment, experimentId]);

  // Experiments rejected before reaching the live/launch phase have no timeline
  // data. Block direct URL access and redirect to the experiments list.
  useEffect(() => {
    if (!experiment) return;
    if (experiment.status !== ExperimentStatus.REJECTED) return;
    if (
      experiment.experimentPhase != null &&
      PRE_LAUNCH_PHASES.includes(experiment.experimentPhase)
    ) {
      void navigate({ to: '/experiments', replace: true });
    }
  }, [experiment, navigate]);

  if (isLoading) {
    return (
      <PageShell experimentId={experimentId} view={view} sensitivity={FIXED_SENSITIVITY}>
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-8 w-96" />
        <Skeleton className="h-96 w-full" />
      </PageShell>
    );
  }

  if (isError || !experiment) {
    return (
      <PageShell
        experimentId={experimentId}
        view={view}
        sensitivity={FIXED_SENSITIVITY}
        centerContent
      >
        <ExperimentLoadError experimentId={experimentId} error={error} />
      </PageShell>
    );
  }

  // Show the chrome-light "Delivery in Progress" card whenever the live
  // pipeline workflow is genuinely in flight — `pipeline.status` is the
  // source of truth, not `experimentPhase`. `experimentPhase` lags
  // (advances only on success) and is stale after a retry: e.g. a prior
  // run stamped LIVE, then a retry kicks the pipeline back to IN_PROGRESS
  // but the phase stays LIVE until delivery finishes again. We surface
  // the spinner on IN_PROGRESS and AWAITING_APPROVAL regardless of phase,
  // plus the legacy phase=LAUNCH + unknown-status case for initial loads
  // and prior runs that haven't been polled yet.
  const pipelineInFlight =
    pipeline?.status === PipelineStatus.IN_PROGRESS ||
    pipeline?.status === PipelineStatus.AWAITING_APPROVAL;
  const isDelivering =
    pipelineInFlight ||
    (experiment.experimentPhase === ExperimentPhase.LAUNCH && pipeline?.status === undefined);
  // Actual LLM spend exists once Enrichment has started: poll while it runs, fetch once
  // more when it settles (completed / failed, or the pipeline is no longer in flight).
  const enrichmentStageStatus = pipeline?.stages?.find((s) => s.name === 'Enrichment')?.status;
  const costEnrichmentPhase: CostActualPhase | null =
    enrichmentStageStatus === 'in_progress' ||
    enrichmentStageStatus === 'completed' ||
    enrichmentStageStatus === 'failed'
      ? enrichmentStageStatus === 'in_progress' && pipelineInFlight
        ? 'running'
        : 'done'
      : null;

  return (
    <PageShell
      experimentId={experimentId}
      view={view}
      sensitivity={FIXED_SENSITIVITY}
      hideChat={isDelivering}
    >
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          void navigate({
            search: (prev) => ({ ...prev, tab: value as ExperimentDetailTab }),
            replace: true,
          });
        }}
        className="space-y-6"
      >
        <ExperimentDetailHeader
          experiment={experiment}
          activeTab={activeTab}
          segment={segment}
          onSegmentChange={setSegment}
          isDelivering={isDelivering}
          // pipelineStatus={pipeline?.status ?? null}
        />

        {/*
          Random-fallback caveat sits below the header so it shows on every tab
          (timeline, details/performance, details/health) without being squeezed
          into a chart panel. Renders nothing for splitMode=SCORED / null, so
          the layout cost is zero on the happy path.
        */}
        <RandomFallbackBanner splitMode={experiment.splitMode} />

        {/* One manual Retry for any panel whose analytics read timed out (no auto-retry). */}
        <AnalyticsTimeoutBanner experimentId={experimentId} />

        <TabsContent value="timeline" className="mt-0 space-y-4">
          {isDelivering ? (
            // PDF row 12a + §4: after APPROVED but before delivery completes,
            // `experimentPhase` is LAUNCH. The Timeline data the API would
            // return at this point is mid-flight — show a dedicated waiting
            // card instead of a half-loaded list. Once delivery finishes
            // (phase → LIVE) we fall through to the real Timeline view.
            // The pipeline may also be paused at the pre-enrichment cost gate —
            // banner above the stage card, estimate (and approver actions) below it.
            <>
              <CostApprovalPendingBanner workflowId={pipeline?.workflowId} />
              <DeliveryInProgressCard stages={pipeline?.stages} />
              <CostEstimateCard
                workflowId={pipeline?.workflowId}
                awaitingEstimate={
                  pipelineInFlight &&
                  (pipeline.stages?.some(
                    (s) => s.name === 'Curation' && s.status === 'completed',
                  ) ??
                    false) &&
                  !pipeline.stages?.some(
                    (s) =>
                      s.name === 'Enrichment' &&
                      (s.status === 'in_progress' || s.status === 'completed'),
                  )
                }
                enrichmentPhase={costEnrichmentPhase}
              />
            </>
          ) : (
            <ExperimentTimeline
              checkpoints={checkpoints}
              experimentId={experimentId}
              isLoading={checkpointsLoading}
              isError={checkpointsError}
            />
          )}
        </TabsContent>

        <TabsContent value="details" className="mt-0 space-y-4">
          <ExperimentDetailsTab
            experimentId={experimentId}
            subTab={detailsSubTab}
            onSubTabChange={(value) => {
              void navigate({
                search: (prev) => ({ ...prev, subTab: value }),
                replace: true,
              });
            }}
            showViewDropdown={showViewDropdown}
            viewMode={activeViewMode}
            onViewModeChange={setUserViewMode}
            grain={FIXED_GRAIN}
            sensitivity={FIXED_SENSITIVITY}
            segment={segmentFilter}
          />
        </TabsContent>

        <TabsContent value="products" className="mt-0 space-y-4">
          {/* Export the enriched catalog for this experiment. sourceWorkflowId is
              the pipeline's Temporal slug (from pipeline/status), not the experiment id. */}
          <div className="flex items-center justify-end gap-3">
            {/* This tab renders the preview with `showHeader={false}`, so there's no
                DiffLegend to host the switch — mount it alongside the export button. */}
            {canCompare && (
              <CompareViewToggle mode={productsViewMode} onModeChange={setProductsViewMode} />
            )}
            <ExportDownloadButton
              sourceWorkflowId={pipeline?.workflowId ?? null}
              label={experiment.name}
              connector="GMC"
            />
          </div>
          <CompareViewCtx.Provider
            value={
              canCompare ? { mode: productsViewMode, onModeChange: setProductsViewMode } : null
            }
          >
            <EnrichmentPreviewProducts
              sessionId={experimentId}
              showHeader={false}
              connectionId={gmcConnectionId}
              syncId={experiment.syncId}
            />
          </CompareViewCtx.Provider>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}

interface PageShellProps {
  experimentId: string;
  view: ChatbotView;
  sensitivity: AnalyticsSensitivity;
  children: React.ReactNode;
  centerContent?: boolean;
  /**
   * Drops the Lily Agent side panel for "minimal chrome" states (currently
   * `experimentPhase === LAUNCH`). The right column expands to fill the
   * viewport so the delivery-in-progress card centers cleanly.
   */
  hideChat?: boolean;
}

/** Below this width the pinned chat column collapses into a floating toggle
 *  + Sheet overlay — there isn't enough room to keep both the chat panel and
 *  the tab content comfortably readable side by side. */
const CHAT_COLLAPSE_QUERY = '(max-width: 1079px)';

/**
 * Two-column page shell — Lily Agent panel on the left, tab/content on the
 * right with the project's brand gradient. Both columns scroll independently
 * so the chatbot stays anchored as the user scrolls long readouts.
 *
 * Above 1080px the chat panel is pinned (subject to `hideChat`, see below).
 * Below 1080px the pinned column is dropped entirely and replaced by a
 * floating button that opens the same chat panel in a left-anchored Sheet —
 * both mounts read/write the same `useAgentPanelStore` context, so the
 * conversation survives the pinned ↔ Sheet transition and resizing across
 * the breakpoint.
 *
 * `hideChat` collapses to a single column for the delivery-in-progress state
 * where chat is intentionally suppressed (also hides the floating toggle).
 */
function PageShell({
  experimentId,
  view,
  sensitivity,
  children,
  centerContent = false,
  hideChat = false,
}: PageShellProps) {
  const isNarrow = useMediaQuery(CHAT_COLLAPSE_QUERY);
  const [isChatSheetOpen, setIsChatSheetOpen] = useState(false);
  // Derived (not stored) so a resize back above the breakpoint closes the
  // Sheet immediately — the pinned panel never reappears alongside an
  // already-open overlay copy.
  const isSheetOpen = isChatSheetOpen && isNarrow;

  return (
    /* `overflow-hidden` is what keeps the two columns scrolling internally.
     * Without it the right column's `overflow-y-auto` never engages: it grows to
     * fit its content, the document scrolls instead, and anything past the
     * gradient's own height renders as bare white below the page background.
     * Matches the pattern used by the onboarding / catalog-operations /
     * item-setup layouts. */
    <div className="bg-gradient-page relative flex h-full overflow-hidden">
      {/*
       * `overflow-hidden` on AgentPanelShell clips BaseChatPanel's internal scroll
       * regions. SlidingPanel animates the entry/exit; shrink-0 keeps the panel at
       * its natural width during the transition. Not rendered at all below the
       * collapse breakpoint — the floating button + Sheet take over instead.
       */}
      {!isNarrow && (
        <SlidingPanel open={!hideChat} side="left" className="shrink-0">
          <AgentPanelShell ariaLabel="Lily Agent" className="overflow-hidden">
            <ExperimentChatbotPanel
              experimentId={experimentId}
              view={view}
              sensitivity={sensitivity}
            />
          </AgentPanelShell>
        </SlidingPanel>
      )}
      {/* `min-w-0` lets the wide analytics tables scroll inside their own cards
          instead of widening this column past the flex row. */}
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div
          className={
            centerContent
              ? 'flex min-h-full items-center justify-center p-6'
              : 'min-h-full space-y-6 p-6'
          }
        >
          {children}
        </div>
      </div>

      {isNarrow && !hideChat && (
        <Button
          type="button"
          aria-label="Open Lily Agent"
          size="icon"
          onClick={() => {
            setIsChatSheetOpen(true);
          }}
          className="fixed right-6 bottom-6 z-40 h-12 w-12 rounded-full shadow-lg"
        >
          <Sparkles className="h-5 w-5" aria-hidden="true" />
        </Button>
      )}

      <Sheet open={isSheetOpen} onOpenChange={setIsChatSheetOpen}>
        <SheetContent
          side="left"
          className="flex w-full max-w-[420px] flex-col gap-0 p-0 sm:max-w-[420px]"
        >
          <SheetTitle className="sr-only">Lily Agent</SheetTitle>
          <AgentPanelShell ariaLabel="Lily Agent" className="!w-full overflow-hidden !border-r-0">
            <ExperimentChatbotPanel
              experimentId={experimentId}
              view={view}
              sensitivity={sensitivity}
            />
          </AgentPanelShell>
        </SheetContent>
      </Sheet>
    </div>
  );
}
